import React from 'react';
import PropTypes from 'prop-types';
import { FaFilePowerpoint, FaFileAlt } from 'react-icons/fa';
import styles from './ExecSummaryLink.module.css';
import { ARTIFACT_KINDS, ARTIFACT_LABELS } from '../../../../services/incidentArtifactService';

const GLYPHS = {
  [ARTIFACT_KINDS.EXEC_SUMMARY_SLIDE]: FaFilePowerpoint,
  [ARTIFACT_KINDS.EXEC_SUMMARY_DOCUMENT]: FaFileAlt,
};

// Only https links are rendered, so a corrupted stored value can never become a javascript:
// or data: href. The server validates on save; this is the second line.
const isHttps = (url) => {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * ExecSummaryLink - the icon that opens an incident's Exec Summary Slide or Document
 * (US2135577, US2174775). Renders nothing when there is no usable link, or a same-width spacer
 * when `reserveSpace` is set so a list's other items do not shift between rows with and without
 * one.
 *
 * It tracks nothing itself: the caller owns which event a click is, and passes it as `onOpen`.
 * The list and the incident page report to different events, and one must not count the other.
 *
 * Its host (a list row or a card) toggles on click AND on Enter/Space keydown, calling
 * preventDefault on the latter, so both are stopped here: a keydown left to bubble would be
 * cancelled and Enter on the focused link would never navigate.
 */
const ExecSummaryLink = ({ url, kind = ARTIFACT_KINDS.EXEC_SUMMARY_SLIDE, onOpen, reserveSpace = false }) => {
  const label = ARTIFACT_LABELS[kind];
  const Glyph = GLYPHS[kind];
  if (!label || !url || !isHttps(url)) {
    return reserveSpace ? <span className={styles.spacer} aria-hidden="true" /> : null;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.link}
      aria-label={label}
      title={label}
      data-testid="exec-summary-link"
      data-kind={kind}
      onClick={(e) => {
        e.stopPropagation();
        if (onOpen) onOpen();
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Glyph aria-hidden="true" />
    </a>
  );
};

ExecSummaryLink.propTypes = {
  url: PropTypes.string,
  kind: PropTypes.oneOf(Object.values(ARTIFACT_KINDS)),
  onOpen: PropTypes.func,
  reserveSpace: PropTypes.bool,
};

export default ExecSummaryLink;
